import {
  BILLING_STATUS,
  SECTION_STATUS,
  TypeBillingStatus,
  TypeProductDecoratedScheduledItem,
  TypeScheduledItemRecord,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import {
  ProductAgeUnits,
  TypeUserRecord,
} from '@/lib/api/accountManager/types';

import { PaymentOptionEnum } from '@/hooks/subFlowAnalytics';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import {
  generateCancelSpecialSubscriptionZendeskLink,
  generateSkipPrepaidKitZendeskLink,
} from '@/utils/zendesk';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import { DisplayDateFormat } from '@/utils/dates/constants';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';

type TypeBillingDate = {
  billing_status: TypeBillingStatus;
  scheduled_date: string;
  locale: string | undefined;
  prepaidLabel: string;
};

type TypeFormattedScheduledDate = {
  scheduled_date: string;
  locale: string | undefined;
};

export function _getActionLinks(
  t: TypeAccountSettingsPageTranslations,
  locale: string | undefined,
  user: TypeUserRecord,
  isCancelSaverProgramEnabled: boolean,
  subscription: TypeSubscriptionRecord,
  nextUnprocessedItem?: TypeScheduledItemRecord,
) {
  const customerSkipKitLink =
    nextUnprocessedItem &&
    (isCancelSaverProgramEnabled
      ? `/account/subscriptions/${subscription.uuid}/modify/skip/${nextUnprocessedItem.uuid}`
      : constructLegacyCustomerPortalLink(locale, [
          user.uuid,
          'account-settings',
          'skip',
          subscription.uuid,
          nextUnprocessedItem.uuid,
        ]));

  const reactivateSubscriptionLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'account-settings',
    'reactivate',
    subscription.uuid,
  ]);

  const customerCancelSubscriptionLink = isCancelSaverProgramEnabled
    ? `/account/subscriptions/${subscription.uuid}/skip-cancel`
    : constructLegacyCustomerPortalLink(locale, [
        user.uuid,
        'account-settings',
        'cancel',
        subscription.uuid,
      ]);

  const removeBookBundleAddonLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'account-settings',
    'cancel-add-on',
    'books_addon',
    subscription.uuid,
  ]);

  const cxSkipKitLink = generateSkipPrepaidKitZendeskLink(
    t.skipKitZendeskSubjectLine,
    locale,
  );
  const cxCancelSubscriptionLink = generateCancelSpecialSubscriptionZendeskLink(
    subscription,
    locale,
  );

  const editLanguageSettingsLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'account-settings',
    'language',
    subscription.uuid,
  ]);

  const prepayAndSaveLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'subscriptions',
    subscription.uuid,
    'prepay-extension',
  ]);

  const continueGiftLink = constructLegacyCustomerPortalLink(locale, [
    user.uuid,
    'subscriptions',
    subscription.uuid,
    'gift-extension',
  ]);

  return {
    customerSkipKitLink,
    cxSkipKitLink,
    reactivateSubscriptionLink,
    customerCancelSubscriptionLink,
    cxCancelSubscriptionLink,
    removeBookBundleAddonLink,
    editLanguageSettingsLink,
    prepayAndSaveLink,
    continueGiftLink,
  };
}

export function _getFormattedScheduledDate(
  scheduleDateInfo: TypeFormattedScheduledDate,
) {
  const { scheduled_date, locale } = scheduleDateInfo;
  return formatDateForDisplay(
    scheduled_date,
    locale,
    DisplayDateFormat.ShortMonthAndDay,
  );
}

export function _getBillingDate(billingInfo: TypeBillingDate) {
  const { billing_status, scheduled_date, locale, prepaidLabel } = billingInfo;
  if (billing_status === BILLING_STATUS.PREPAY) {
    return prepaidLabel;
  }

  return _getFormattedScheduledDate({ scheduled_date, locale });
}

export function _isPrepaidBundle(scheduledItem?: TypeScheduledItemRecord) {
  if (scheduledItem?.billing_status === BILLING_STATUS.PREPAY) {
    return true;
  }

  return false;
}

export function _findChildForSubscription(
  subscription: TypeSubscriptionRecord,
  user: TypeUserRecord,
) {
  const childUuid = subscription?.child_uuid;
  return user.children?.find((child) => child.uuid === childUuid);
}

export function canPrepayForSubscription(
  subscription?: TypeSubscriptionRecord,
) {
  const activeSection = subscription?.schedule_sections?.find(
    (s) => s.section_status === SECTION_STATUS.ACTIVE,
  );

  const upcomingItems = activeSection?.scheduled_items?.filter(
    (i) =>
      !!i.scheduled_date &&
      i.billing_status === BILLING_STATUS.AWAITING_PAYMENT,
  );

  return upcomingItems && upcomingItems.length >= 2;
}

export function extractMinAndMaxAgeRange(
  displayAgeRange: string | null | undefined,
) {
  if (!displayAgeRange) return { minRange: null, maxRange: null };

  const matches = displayAgeRange.match(/(\d+)\s*-\s*(\d+)/);
  if (!matches) return { minRange: null, maxRange: null };

  const minRange = parseInt(matches[1], 10);
  const maxRange = parseInt(matches[2], 10);

  return { minRange, maxRange };
}

export function getSubscriptionPaymentOption(
  subscription: TypeSubscriptionRecord,
  nextUnprocessedItem: TypeScheduledItemRecord | undefined,
) {
  const hasBundleId = subscription.bundle_discount_uuid;

  if (_isPrepaidBundle(nextUnprocessedItem) && !hasBundleId) {
    return PaymentOptionEnum.PUF;
  }

  if (hasBundleId) {
    return PaymentOptionEnum.RECURRING_PUF;
  }

  return PaymentOptionEnum.PAYGO;
}

export function generateSubscriptionEventPayload(
  productDecoratedItem: TypeProductDecoratedScheduledItem,
  subscription: TypeSubscriptionRecord,
  nextUnprocessedItem: TypeScheduledItemRecord | undefined,
) {
  const result = extractMinAndMaxAgeRange(
    productDecoratedItem.product?.display_age_range,
  );

  const product = {
    url: productDecoratedItem.product?.url,
    productId: productDecoratedItem.product_uuid,
    sku: productDecoratedItem.product?.default_variant_sku || '',
    category: productDecoratedItem.product?.product_type || '',
    name: productDecoratedItem.product?.name,
    ageRangeMin: result?.minRange,
    ageRangeMax: result?.maxRange,
    ageUnits: ProductAgeUnits.MONTH,
    price: productDecoratedItem.product?.price,
    quantity: productDecoratedItem.product?.products?.length,
  };

  const paymentOption = getSubscriptionPaymentOption(
    subscription,
    nextUnprocessedItem,
  );

  return {
    subscriptionUUID: subscription.uuid,
    paymentOption,
    bundleSize: subscription.schedule_sections[0].scheduled_items.filter(
      (item) => item.process_status !== 'not_applicable',
    ).length,
    product: product,
    url: window.location.href,
  };
}
